import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { RgbaStringColorPicker } from 'react-colorful'

import { useFormContext } from 'react-hook-form'
import { FormValues } from './LootHistoryResponseTypeModal'
import { useClickOutside } from 'app/frontend/base/helpers/hooks'
import { CircleQuestionMark, MoreVertical } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import Select from 'app/frontend/base/components/Select'
import Card from 'app/frontend/base/components/Card'

type Props = {
  dragging: boolean
  responseType: wowaudit.LootHistoryResponseType & { index: number }
  staticType?: boolean
  typeOptions: wowaudit.LootHistoryResponseType[]
}

const LootHistorySingleResponseType = ({
  dragging,
  responseType,
  staticType = false,
  typeOptions,
}: Props) => {
  const { setValue, watch } = useFormContext<FormValues>()
  const values = watch()
  const colorPicker = useRef<HTMLDivElement>(null)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [selectedColor, setSelectedColor] = useState(responseType.rgba)
  const close = useCallback(() => setShowColorPicker(false), [])
  useClickOutside(colorPicker, close)

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: responseType.id,
  })

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined

  useEffect(() => {
    if (!showColorPicker && selectedColor !== responseType.rgba) {
      setValue(
        `loot_history_response_types_attributes.${responseType.index}.rgba`,
        selectedColor,
      )
    }
  }, [showColorPicker])

  const disabled = values.loot_history_response_types_attributes.some(
    (type) => type.synonym_of_id === responseType.id,
  )

  return (
    <Card
      className={`py-1 px-2 flex items-center gap-1.5 justify-between cursor-auto ${
        dragging ? 'pointer-events-none' : ''
      } ${transform ? 'opacity-70 relative z-50' : ''}`}
      ref={setNodeRef}
      style={style}
      {...(staticType ? {} : attributes)}
    >
      {!staticType && (
        <MoreVertical className='cursor-pointer' size={14} {...listeners} />
      )}
      <div
        className='w-4 h-4'
        style={{ backgroundColor: responseType.rgba }}
        onClick={() => setShowColorPicker(true)}
      >
        {showColorPicker && (
          <div className='popover' ref={colorPicker}>
            <RgbaStringColorPicker
              color={responseType.rgba}
              onChange={(value) => setSelectedColor(value)}
            />
          </div>
        )}
      </div>
      <span
        className={`grow ${responseType.synonym_of_id ? 'opacity-50' : ''}`}
      >
        {responseType.name}
      </span>
      {disabled && (
        <Tooltip>
          <TooltipContent side='left' className='max-w-60'>
            Another type is already merged with this one, so this cannot be
            merged with something else
          </TooltipContent>
          <TooltipTrigger className='mr-4'>
            <CircleQuestionMark size={16} />
          </TooltipTrigger>
        </Tooltip>
      )}

      {disabled ? null : (
        <Select
          options={typeOptions
            .filter((type) => type.id !== responseType.id)
            .map((option) => ({
              value: option.id.toString(),
              label: option.name,
              className: option.excluded ? 'text-destructive-foreground' : '',
            }))}
          value={responseType.synonym_of_id?.toString() ?? undefined}
          placeholder=''
          isClearable={true}
          inModal={true}
          className={`w-40 h-6 font-normal`}
          setValue={(value) =>
            setValue(
              `loot_history_response_types_attributes.${responseType.index}.synonym_of_id`,
              value ? parseInt(value) : null,
            )
          }
        />
      )}
    </Card>
  )
}

export default LootHistorySingleResponseType
